import { useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui'
import { getCompanyColor } from '@/features/shifts/utils/companyColor'

/** Sentinel scope used by the company tabs for "no company / org-wide" notifications. */
export const GENERAL_COMPANY_SCOPE = '__general__'
const MAX_VISIBLE_COMPANY_TABS = 10

export interface NotificationCompanyTab {
  /** Company id, or {@link GENERAL_COMPANY_SCOPE} for the org-wide bucket. */
  id: string
  name: string
  total: number
  unread: number
}

export interface NotificationCompanyTabsProps {
  tabs: NotificationCompanyTab[]
  /** `null` means the "All companies" tab is active. */
  selectedId: string | null
  allUnread: number
  allTotal: number
  onSelect: (id: string | null) => void
}

function CountBadge({
  unread,
  total,
}: {
  unread: number
  total: number
}): React.ReactElement {
  const showUnread = unread > 0
  const value = showUnread ? unread : total
  return (
    <Badge
      variant={showUnread ? 'danger' : 'neutral'}
      className="min-w-7 justify-center px-1.5"
    >
      {value > 99 ? '99+' : value}
    </Badge>
  )
}

/**
 * Company selector strip for the Notifications page. Renders an "All companies"
 * toggle plus one chip per company, each with a badge showing unread (or total)
 * counts — mirroring the company badge pattern used across the app.
 */
export function NotificationCompanyTabs({
  tabs,
  selectedId,
  allUnread,
  allTotal,
  onSelect,
}: NotificationCompanyTabsProps): React.ReactElement {
  const [overflowOpen, setOverflowOpen] = useState(false)
  const { visibleTabs, overflowTabs } = useMemo(() => {
    if (tabs.length <= MAX_VISIBLE_COMPANY_TABS) {
      return { visibleTabs: tabs, overflowTabs: [] }
    }

    const selected = tabs.filter((tab) => selectedId === tab.id)
    const rest = tabs.filter((tab) => selectedId !== tab.id)
    const visible = [...selected, ...rest].slice(0, MAX_VISIBLE_COMPANY_TABS)
    const visibleIds = new Set(visible.map((tab) => tab.id))
    return {
      visibleTabs: visible,
      overflowTabs: tabs.filter((tab) => !visibleIds.has(tab.id)),
    }
  }, [selectedId, tabs])

  const renderTab = (tab: NotificationCompanyTab): React.ReactElement => {
    const active = selectedId === tab.id
    const isGeneral = tab.id === GENERAL_COMPANY_SCOPE
    const color = getCompanyColor(isGeneral ? '' : tab.id)
    return (
      <button
        key={tab.id}
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => onSelect(tab.id)}
        className={cn(
          'inline-flex h-9 cursor-pointer items-center gap-2 rounded-xl border px-3.5 text-xs font-semibold transition-all duration-150',
          active
            ? cn(
                isGeneral ? 'bg-gray-800 text-white' : cn(color.bg, color.text),
                'border-transparent shadow-[0_10px_24px_rgba(15,23,42,0.08)]'
              )
            : 'border-gray-200 bg-white/90 text-gray-700 hover:border-gray-300 hover:bg-white dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:hover:border-gray-600'
        )}
      >
        {!isGeneral && (
          <span
            className={cn('h-1.5 w-1.5 rounded-full', color.dotBg)}
            aria-hidden
          />
        )}
        <span className="max-w-[12rem] truncate">{tab.name}</span>
        <CountBadge unread={tab.unread} total={tab.total} />
      </button>
    )
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      role="tablist"
      aria-label="Filter notifications by company"
    >
      <button
        type="button"
        role="tab"
        aria-selected={selectedId === null}
        onClick={() => onSelect(null)}
        className={cn(
          'inline-flex h-9 cursor-pointer items-center gap-2 rounded-xl border px-3.5 text-xs font-semibold transition-all duration-150',
          selectedId === null
            ? 'border-transparent bg-primary text-white shadow-[0_10px_24px_rgba(15,23,42,0.12)]'
            : 'border-gray-200 bg-white/90 text-gray-700 hover:border-gray-300 hover:bg-white dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:hover:border-gray-600'
        )}
      >
        All companies
        <CountBadge
          unread={allUnread}
          total={allTotal}
        />
      </button>

      {visibleTabs.map(renderTab)}
      {overflowTabs.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOverflowOpen((open) => !open)}
            className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-xl border border-gray-200 bg-white/90 px-3.5 text-xs font-semibold text-gray-700 transition-all duration-150 hover:border-gray-300 hover:bg-white dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:hover:border-gray-600"
            aria-expanded={overflowOpen}
            aria-haspopup="menu"
          >
            +{overflowTabs.length} more
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          </button>
          {overflowOpen && (
            <div
              role="menu"
              className="absolute left-0 z-30 mt-2 max-h-72 w-80 overflow-y-auto rounded-xl border border-gray-200 bg-white p-2 shadow-xl dark:border-gray-700 dark:bg-gray-800"
            >
              {overflowTabs.map((tab) => {
                const active = selectedId === tab.id
                const isGeneral = tab.id === GENERAL_COMPANY_SCOPE
                const color = getCompanyColor(isGeneral ? '' : tab.id)
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    onClick={() => {
                      onSelect(tab.id)
                      setOverflowOpen(false)
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold transition-colors',
                      active
                        ? cn(isGeneral ? 'bg-gray-800 text-white' : cn(color.bg, color.text))
                        : 'text-gray-700 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700'
                    )}
                  >
                    {!isGeneral && (
                      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', color.dotBg)} />
                    )}
                    <span className="min-w-0 flex-1 truncate">{tab.name}</span>
                    <CountBadge unread={tab.unread} total={tab.total} />
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

NotificationCompanyTabs.displayName = 'NotificationCompanyTabs'
